import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categoryArticleImages: Record<string, string> = {
  'article-entertainment-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20microphone%20and%20spotlight%2C%20soft%20natural%20lighting%2C%20modern%20entertainment%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=800&height=500&seq=cat-article-entertainment-01&orientation=landscape',
  'article-society-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20cityscape%20silhouette%2C%20soft%20natural%20lighting%2C%20modern%20lifestyle%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=800&height=500&seq=cat-article-society-01&orientation=landscape',
  'article-internet-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20social%20media%20icons%20and%20network%20visualization%2C%20soft%20natural%20lighting%2C%20modern%20digital%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=800&height=500&seq=cat-article-internet-01&orientation=landscape',
  'article-tech-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20laptop%20and%20gadgets%20on%20wooden%20desk%2C%20soft%20natural%20lighting%2C%20modern%20tech%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=800&height=500&seq=cat-article-tech-01&orientation=landscape',
  'article-entertainment-02': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20stage%20curtain%20and%20spotlight%20glow%2C%20soft%20natural%20lighting%2C%20modern%20performance%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=800&height=500&seq=cat-article-entertainment-02&orientation=landscape',
  'article-fashion-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20fabric%20textures%20and%20accessories%2C%20soft%20natural%20lighting%2C%20modern%20fashion%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=800&height=500&seq=cat-article-fashion-01&orientation=landscape',
  'article-esports-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20gaming%20controller%20and%20colorful%20LED%20lights%2C%20soft%20natural%20lighting%2C%20modern%20esports%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=800&height=500&seq=cat-article-esports-01&orientation=landscape',
  'article-finance-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20coins%20and%20growth%20chart%2C%20soft%20natural%20lighting%2C%20modern%20finance%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=800&height=500&seq=cat-article-finance-01&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<string>(slug || 'yule-bagua');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  const allCategoryArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '分类浏览', to: '/category/yule-bagua' },
    { label: currentCategory?.name || '' },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                热门<strong className="text-primary-600">资讯分类</strong>
              </h1>
              <p className="text-sm text-foreground-600">
                选择您感兴趣的分类，浏览相关资讯内容
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                  <span className={`ml-1.5 text-xs ${activeCategory === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                    {cat.articleCount}
                  </span>
                </button>
              ))}
            </div>

            {currentCategory && (
              <div className="mb-8 p-5 bg-accent-50 rounded-lg border border-accent-100/70">
                <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-1">
                  {currentCategory.name}
                </h2>
                <p className="text-sm text-foreground-600">{currentCategory.description}</p>
              </div>
            )}

            {allCategoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {allCategoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 group cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-background-200">
                      <img
                        src={categoryArticleImages[article.imageSeq]}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                          {article.categoryName}
                        </span>
                        <span className="text-xs text-foreground-400">{article.publishedAt}</span>
                      </div>
                      <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">{article.author}</span>
                        <span className="flex items-center gap-1 text-xs text-foreground-400">
                          <i className="ri-eye-line"></i> {article.readCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-14">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-200 rounded-full">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}